import { z } from 'zod';
import { getItemById } from '../items/items';
import { getSkillById } from '../skills/skills';
import {
  activeStatusSchema,
  characterStatsSchema,
  factionSchema,
  inventoryStackSchema,
} from '../characters/characterSchemas';
import rawProtagonist from './protagonist.json';

export const protagonistSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    title: z.string().min(1),
    description: z.string().min(1),
    faction: factionSchema,
    level: z.number().int().positive(),
    exp: z.number().int().nonnegative(),
    statPoints: z.number().int().nonnegative(),
    hp: z.literal(1),
    maxHp: z.literal(1),
    mana: z.number().int().nonnegative(),
    maxMana: z.number().int().positive(),
    gold: z.number().int().nonnegative(),
    stats: characterStatsSchema,
    skillIds: z.array(z.string().min(1)).min(1),
    statuses: z.array(activeStatusSchema).default([]),
    inventory: z.array(inventoryStackSchema).default([]),
  })
  .refine((hero) => hero.mana <= hero.maxMana, {
    message: 'Protagonist mana cannot exceed maxMana',
  });

export type ProtagonistDef = z.infer<typeof protagonistSchema>;
export const protagonist = protagonistSchema.parse(rawProtagonist);

const inventoryIds = new Set<string>();
for (const stack of protagonist.inventory) {
  if (!getItemById(stack.itemId)) {
    throw new Error(`Protagonist references unknown item ${stack.itemId}`);
  }
  if (inventoryIds.has(stack.itemId)) {
    throw new Error(`Protagonist has duplicate inventory stack ${stack.itemId}`);
  }
  inventoryIds.add(stack.itemId);
}

for (const skillId of protagonist.skillIds) {
  const skill = getSkillById(skillId);
  if (!skill) {
    throw new Error(`Protagonist references unknown skill ${skillId}`);
  }
  if (skill.faction !== protagonist.faction) {
    throw new Error(`Protagonist cannot use ${skillId} from faction ${skill.faction}`);
  }
}
